import EXIF from 'exif-js';

/**
 * 根据EXIF方向信息获取旋转角度
 * @param {File} file - 图片文件
 * @returns {Promise<number>} 旋转角度（0, 90, 180, 270）
 */
export function getOrientationFromExif(file) {
  return new Promise((resolve) => {
    // 设置超时，防止EXIF读取失败导致Promise永远不resolve
    const timeout = setTimeout(() => {
      console.warn('[EXIF] 读取EXIF信息超时，使用默认值0（不旋转）');
      resolve(0); // 超时默认返回0（不旋转）
    }, 2000); // 增加超时时间到2秒

    try {
      EXIF.getData(file, function() {
        clearTimeout(timeout);
        const orientation = EXIF.getTag(this, 'Orientation');
        
        // EXIF方向值映射到旋转角度
        // 1: 正常 (0°)
        // 3: 旋转180° (180°)
        // 6: 顺时针旋转90° (需要逆时针旋转270°或顺时针-90°)
        // 8: 逆时针旋转90° (需要顺时针旋转90°)
        const rotationMap = {
          1: 0,
          3: 180,
          6: -90, // 或 270
          8: 90,
        };
        
        const rotationAngle = rotationMap[orientation] || 0;
        
        // 记录日志，便于排查问题
        if (orientation && orientation !== 1) {
          console.log(`[EXIF] 检测到图片EXIF Orientation=${orientation}，需要旋转角度=${rotationAngle}°`);
        } else {
          console.log(`[EXIF] 图片EXIF Orientation=${orientation || '未定义'}，无需旋转`);
        }
        
        resolve(rotationAngle);
      });
    } catch (error) {
      clearTimeout(timeout);
      console.warn('[EXIF] 读取EXIF信息失败:', error);
      resolve(0); // 出错时默认返回0（不旋转）
    }
  });
}

/**
 * 旋转图片
 * 注意：Canvas的toBlob()生成的图片不包含EXIF元数据，确保后端不会重复旋转
 * @param {HTMLImageElement|File|Blob} image - 图片元素、文件或Blob
 * @param {number} degrees - 旋转角度（90, 180, 270, -90等）
 * @returns {Promise<Blob>} 旋转后的图片Blob（不包含EXIF元数据）
 */
export function rotateImage(image, degrees) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    let img = new Image();
    
    // 处理不同类型的输入
    if (image instanceof File || image instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => drawRotatedImage();
        img.onerror = (err) => {
          console.error('[图片旋转] 图片加载失败:', err);
          reject(new Error('图片加载失败'));
        };
        img.src = e.target.result;
      };
      reader.onerror = (err) => {
        console.error('[图片旋转] 文件读取失败:', err);
        reject(new Error('文件读取失败'));
      };
      reader.readAsDataURL(image);
    } else if (image instanceof HTMLImageElement) {
      img = image;
      if (img.complete) {
        drawRotatedImage();
      } else {
        img.onload = () => drawRotatedImage();
        img.onerror = (err) => {
          console.error('[图片旋转] 图片元素加载失败:', err);
          reject(new Error('图片元素加载失败'));
        };
      }
    } else if (typeof image === 'string') {
      // 如果是URL字符串
      img.onload = () => drawRotatedImage();
      img.onerror = (err) => {
        console.error('[图片旋转] 图片URL加载失败:', err);
        reject(new Error('图片URL加载失败'));
      };
      img.src = image;
    } else {
      reject(new Error('不支持的图片类型'));
      return;
    }

    function drawRotatedImage() {
      try {
        // 计算旋转后的画布尺寸
        const radians = (degrees * Math.PI) / 180;
        const cos = Math.abs(Math.cos(radians));
        const sin = Math.abs(Math.sin(radians));
        
        const newWidth = Math.ceil(img.width * cos + img.height * sin);
        const newHeight = Math.ceil(img.width * sin + img.height * cos);

        canvas.width = newWidth;
        canvas.height = newHeight;

        // 设置高质量渲染
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // 移动到画布中心
        ctx.translate(newWidth / 2, newHeight / 2);
        // 旋转
        ctx.rotate((degrees * Math.PI) / 180);
        // 绘制图片
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        // 转换为Blob
        // 注意：Canvas的toBlob()生成的图片不包含EXIF元数据，这是期望的行为
        // 这样可以确保后端不会根据EXIF Orientation再次旋转图片
        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log(`[图片旋转] 旋转完成，角度: ${degrees}°, 原始尺寸: ${img.width}x${img.height}, 新尺寸: ${newWidth}x${newHeight}`);
              resolve(blob);
            } else {
              console.error('[图片旋转] Canvas转换为Blob失败');
              reject(new Error('图片转换失败'));
            }
          },
          'image/jpeg',
          0.95 // 质量
        );
      } catch (error) {
        console.error('[图片旋转] 绘制图片时出错:', error);
        reject(error);
      }
    }
  });
}

/**
 * 修正图片方向（根据EXIF自动旋转）
 * 注意：Canvas的toBlob()生成的图片不包含EXIF元数据，确保后端不会重复旋转
 * @param {File} file - 原始图片文件
 * @returns {Promise<File>} 修正后的图片文件（不包含EXIF Orientation标签）
 */
export async function fixImageOrientation(file) {
  const startTime = Date.now();
  try {
    console.log(`[图片方向修正] 开始处理文件: ${file.name}, 大小: ${(file.size / 1024).toFixed(2)}KB`);
    
    // 读取EXIF方向信息
    const rotationAngle = await getOrientationFromExif(file);
    
    if (rotationAngle === 0) {
      // 无需旋转，直接返回原文件
      console.log(`[图片方向修正] 图片无需旋转，直接返回原文件`);
      return file;
    }

    console.log(`[图片方向修正] 开始旋转图片，角度: ${rotationAngle}°`);
    
    // 旋转图片（Canvas旋转后生成的Blob不包含EXIF元数据）
    const rotatedBlob = await rotateImage(file, rotationAngle);
    
    // 将Blob转换为File对象
    // 注意：Canvas生成的Blob不包含EXIF元数据，确保后端不会重复旋转
    const correctedFile = new File([rotatedBlob], file.name, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    
    const processTime = Date.now() - startTime;
    console.log(`[图片方向修正] 图片旋转完成，处理时间: ${processTime}ms, 新文件大小: ${(correctedFile.size / 1024).toFixed(2)}KB`);
    console.log(`[图片方向修正] 注意：旋转后的图片不包含EXIF Orientation标签，后端不会重复旋转`);

    return correctedFile;
  } catch (error) {
    const processTime = Date.now() - startTime;
    console.error(`[图片方向修正] 修正图片方向失败（耗时${processTime}ms），使用原文件:`, error);
    console.warn(`[图片方向修正] 警告：如果原文件包含EXIF Orientation标签，后端可能会再次旋转`);
    return file;
  }
}

/**
 * 将File转换为Base64 URL（用于预览）
 * @param {File} file - 图片文件
 * @returns {Promise<string>} Base64 URL
 */
export function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * 将Blob转换为File对象
 * @param {Blob} blob - Blob对象
 * @param {string} filename - 文件名
 * @returns {File} File对象
 */
export function blobToFile(blob, filename) {
  return new File([blob], filename, {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * 从URL下载图片并转换为File对象
 * @param {string} url - 图片URL
 * @param {string} filename - 文件名
 * @returns {Promise<File>} File对象
 */
export async function urlToFile(url, filename = 'image.jpg') {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return blobToFile(blob, filename);
  } catch (error) {
    console.error('下载图片失败:', error);
    throw error;
  }
}

